import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const SERVICES = [
  { name: 'Commercial Cleaning', path: '/services/commercial-cleaning', desc: 'Commercial-grade facility cleaning' },
  { name: 'Janitorial Services', path: '/services/janitorial', desc: 'Recurring janitorial maintenance' },
  { name: 'Office Cleaning', path: '/services/office-cleaning', desc: 'Office & workspace solutions' },
  { name: 'Floor Care & Maintenance', path: '/services/floor-care', desc: 'Stripping, waxing & polishing' },
  { name: 'Post-Construction Cleaning', path: '/services/post-construction', desc: 'Post-build deep cleaning' },
  { name: 'Specialized Facility Cleaning', path: '/services/specialized', desc: 'Custom facility solutions' },
  { name: 'Professional Staffing', path: '/services/staffing', desc: 'Elite hospitality professionals' },
  { name: 'Valet Parking', path: '/services/valet', desc: 'Premium parking solutions' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  return (
    <nav className={`fixed w-full top-0 z-50 transition-all duration-400 ${scrolled ? 'bg-[#03143A] shadow-lg border-b border-white/10' : 'bg-transparent'}`}>
      {/* Top utility bar - only show when not scrolled */}
      {!scrolled && (
        <div className="hidden lg:block bg-[#03143A]/90 backdrop-blur-sm border-b border-white/10">
          <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
            <div className="flex items-center gap-6">
              <a href="mailto:its@primefacilityservicesgroup.com" className="flex items-center gap-1.5 text-white/60 hover:text-white text-xs transition-colors cursor-pointer">
                <i className="ri-mail-line text-[#C70532]" />
                its@primefacilityservicesgroup.com
              </a>
              <span className="text-white/30 text-xs">|</span>
              <span className="flex items-center gap-1.5 text-white/60 text-xs">
                <i className="ri-map-pin-line text-[#C70532]" />
                8303 Westglen Dr, Houston, TX 77063
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5 text-white/60 text-xs">
                <i className="ri-time-line text-[#C70532]" />
                24/7 Emergency Services Available
              </span>
              <a href="tel:+17133382553" className="flex items-center gap-1.5 bg-[#C70532] text-white text-xs font-bold px-3 py-1 rounded-full hover:bg-[#a80428] transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill" />
                (713) 338-2553
              </a>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex-shrink-0 cursor-pointer">
            <img
              src="https://primefacilityservicesgroup.com/logo-prime-facility-white.png"
              alt="Prime Facility Services Group"
              className="h-12 w-auto"
            />
          </Link>

          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className="text-white/90 hover:text-white text-sm font-semibold tracking-wide transition-colors whitespace-nowrap cursor-pointer relative group">
              Home
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#C70532] group-hover:w-full transition-all duration-300" />
            </Link>

            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <button className="flex items-center gap-1 text-white/90 hover:text-white text-sm font-semibold tracking-wide transition-colors whitespace-nowrap cursor-pointer relative group">
                Services
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`} />
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#C70532] group-hover:w-full transition-all duration-300" />
              </button>
              {servicesOpen && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-76 bg-white rounded-2xl overflow-hidden border border-gray-100 py-2 shadow-2xl">
                  {SERVICES.map((s) => (
                    <Link
                      key={s.path}
                      to={s.path}
                      className="flex items-center gap-3 px-5 py-3 hover:bg-[#FAF8F5] transition-colors group cursor-pointer"
                    >
                      <div className="w-2 h-2 rounded-full bg-[#C70532]/30 group-hover:bg-[#C70532] transition-colors flex-shrink-0" />
                      <div>
                        <span className="text-sm font-semibold text-[#03143A] group-hover:text-[#C70532] transition-colors block">{s.name}</span>
                        <span className="text-xs text-gray-400">{s.desc}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link to="/about" className="text-white/90 hover:text-white text-sm font-semibold tracking-wide transition-colors whitespace-nowrap cursor-pointer relative group">
              About
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#C70532] group-hover:w-full transition-all duration-300" />
            </Link>
            <Link to="/locations/houston" className="text-white/90 hover:text-white text-sm font-semibold tracking-wide transition-colors whitespace-nowrap cursor-pointer relative group">
              Locations
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#C70532] group-hover:w-full transition-all duration-300" />
            </Link>
            <Link to="/contact" className="text-white/90 hover:text-white text-sm font-semibold tracking-wide transition-colors whitespace-nowrap cursor-pointer relative group">
              Contact
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#C70532] group-hover:w-full transition-all duration-300" />
            </Link>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href="tel:+17133382553"
              className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white text-sm font-bold rounded-full border border-white/20 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill text-[#C70532]" />
              </div>
              (713) 338-2553
            </a>
            <Link
              to="/contact"
              className="px-6 py-2.5 bg-[#C70532] hover:bg-[#a80428] text-white text-sm font-bold rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </Link>
          </div>

          <button
            className="lg:hidden text-white p-2 cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-2xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'}`} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-[#03143A] border-t border-white/10 overflow-y-auto max-h-[80vh]">
          <div className="px-6 py-6 space-y-1">
            <Link to="/" className="block text-white font-semibold py-3 border-b border-white/10 cursor-pointer">Home</Link>
            <div>
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                className="flex items-center justify-between w-full text-white font-semibold py-3 border-b border-white/10 cursor-pointer"
              >
                Services
                <i className={`ri-arrow-down-s-line transition-transform duration-300 ${servicesOpen ? 'rotate-180' : ''}`} />
              </button>
              {servicesOpen && (
                <div className="pl-4 py-2 space-y-1">
                  {SERVICES.map((s) => (
                    <Link key={s.path} to={s.path} className="block text-white/75 hover:text-white py-2 text-sm transition-colors cursor-pointer">
                      {s.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Link to="/about" className="block text-white font-semibold py-3 border-b border-white/10 cursor-pointer">About</Link>
            <Link to="/locations/houston" className="block text-white font-semibold py-3 border-b border-white/10 cursor-pointer">Locations</Link>
            <Link to="/contact" className="block text-white font-semibold py-3 border-b border-white/10 cursor-pointer">Contact</Link>
            <div className="pt-4 space-y-3">
              <a href="tel:+17133382553" className="flex items-center justify-center gap-2 w-full py-3 bg-white/10 text-white rounded-full font-semibold cursor-pointer whitespace-nowrap">
                <i className="ri-phone-fill text-[#C70532]" /> (713) 338-2553
              </a>
              <Link to="/contact" className="block text-center w-full py-3 bg-[#C70532] text-white rounded-full font-bold cursor-pointer whitespace-nowrap">
                Get Free Quote
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
